import { getBackendUrl } from "../lib/utils";
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Play, Image as ImageIcon, Video } from 'lucide-react';
import { cn, getImageUrl } from '../lib/utils';
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

const mockImages = [
  '/images/renders/render1.jpg',
  '/images/renders/render2.jpg',
  '/images/renders/render3.jpg',
  '/images/renders/render4.jpg',
  '/images/renders/render5.jpg',
];

const mockVideos = [
  {
    id: 'mock-1',
    title: 'İSİLKOOP Tanıtım Filmi',
    description: "Trakya'nın en büyük karma sanayi sitesi projemizi yakından tanıyın.",
    video_url: 'https://www.youtube.com/watch?v=Gsw55Ju4kiQ',
    type: 'video'
  }
];

const fadeInUp = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } }
};

const getYoutubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const getYoutubeEmbedUrl = (url) => {
  const id = getYoutubeId(url);
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

const Gallery = () => {
  const [mediaList, setMediaList] = useState([]);
  const [activeTab, setActiveTab] = useState('photos'); // 'photos' | 'videos'

  useEffect(() => {
    Fancybox.bind('[data-fancybox="gallery"]', {
      Hash: false,
      Thumbs: {
        autoStart: false,
      },
    });

    return () => {
      Fancybox.destroy();
    };
  }, []);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const backendUrl = getBackendUrl();
        const { data } = await axios.get(`${backendUrl}/api/gallery`);
        if (data && data.length > 0) {
          setMediaList(data);
        } else {
          // Fallback to mock data if empty
          const defaults = [
            ...mockImages.map((img, idx) => ({ id: `mock-img-${idx}`, image_url: img, type: 'image' })),
            ...mockVideos
          ];
          setMediaList(defaults);
        }
      } catch (err) {
        // Fallback to mock data on error
        const defaults = [
          ...mockImages.map((img, idx) => ({ id: `mock-img-${idx}`, image_url: img, type: 'image' })),
          ...mockVideos
        ];
        setMediaList(defaults);
      }
    };
    fetchGallery();
  }, []);

  const photos = mediaList.filter(item => item.type === 'image' || !item.type);
  const videos = mediaList.filter(item => item.type === 'video');

  return (
    <div className="pt-32 pb-24 min-h-screen bg-slate-50">
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h4 className="text-blue-600 font-bold uppercase tracking-widest text-sm mb-3">Medya</h4>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 mb-6">Galeri</h1>
          <p className="text-lg text-slate-600 font-medium max-w-2xl mx-auto mb-10">
            Projemizin güncel durumu, tanıtım filmlerimiz ve etkinliklerimizden kareler.
          </p>

          {/* TABS */}
          <div className="inline-flex bg-slate-200/50 p-1.5 rounded-2xl mb-12">
            <button 
              onClick={() => setActiveTab('photos')}
              className={cn(
                "flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-sm transition-all duration-300",
                activeTab === 'photos' ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-800"
              )}
            >
              <ImageIcon size={18} /> Fotoğraflar ({photos.length})
            </button>
            <button 
              onClick={() => setActiveTab('videos')}
              className={cn(
                "flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-sm transition-all duration-300",
                activeTab === 'videos' ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-800"
              )}
            >
              <Video size={18} /> Videolar ({videos.length})
            </button>
          </div>
        </motion.div>

        {activeTab === 'photos' ? (
          <motion.div 
            key="photos"
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.1 } }
            }}
            className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6"
          >
            {photos.map((img, index) => {
              const src = getImageUrl(img.image_url);
              return (
                <motion.a 
                  key={img.id || index}
                  href={src}
                  data-fancybox="gallery"
                  data-caption={img.title || img.description || ''}
                  variants={fadeInUp}
                  className="break-inside-avoid relative group rounded-3xl overflow-hidden cursor-pointer shadow-lg shadow-slate-200/50 border border-slate-100 flex flex-col justify-end block"
                >
                  <img 
                    src={src} 
                    alt={img.title || `İsilkoop Gallery ${index + 1}`}
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-5">
                    <h4 className="text-white font-bold text-sm">{img.title || ''}</h4>
                    {img.description && <p className="text-slate-200 text-xs font-medium mt-1">{img.description}</p>}
                  </div>
                </motion.a>
              );
            })}
          </motion.div>
        ) : (
          <motion.div 
            key="videos"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={videos.length === 1 ? "max-w-3xl mx-auto" : "grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto"}
          >
            {videos.map((vid, idx) => {
              const embedUrl = getYoutubeEmbedUrl(vid.video_url);
              return (
                <div key={vid.id || idx} className="bg-white p-4 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col justify-between h-full hover:shadow-2xl hover:scale-[1.01] transition-all duration-300">
                  <div className="w-full aspect-video rounded-2xl overflow-hidden bg-slate-900 relative">
                    {embedUrl ? (
                      <iframe 
                        width="100%" 
                        height="100%" 
                        src={`${embedUrl}?autoplay=0`} 
                        title={vid.title} 
                        frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs font-semibold">
                        Video yüklenemedi
                      </div>
                    )}
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-extrabold text-slate-900 mb-2">{vid.title}</h3>
                    {vid.description && <p className="text-slate-600 font-medium text-sm leading-relaxed">{vid.description}</p>}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
